import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  inject,
  output,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { AbilityServiceSignal } from '@casl/angular';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { TranslateModule } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { AuthorizationModule } from '@wilson/authorization';
import { FeatureFlagsModule } from '@wilson/feature-flags';
import {
  CELL_VIEW_EXPANDED_HEIGHT_VH,
  MIN_UNASSIGNED_REGION_HEIGHT_PX,
  NumberOfShiftPerDayRecord,
  RoleAction,
  RolePermissionSubject,
  TimelineTimeframe,
  UnassignedShift,
  UnassignedShiftStackByDay,
} from '@wilson/interfaces';
import { LoadingSpinnerModule } from '@wilson/shared-components';
import {
  CheckShiftActivityPermissionsService,
  UnassignedRegionCellViewShiftsDetailsRenderComponent,
  UnassignedRegionSegmentComponent,
  UnassignedRegionToggleComponent,
} from '@wilson/shift-timeline/assignments-gantt-chart';
import {
  CreateNewViaDragAndDropService,
  DragAndDropModeService,
  getRawOffsetXFromEvent,
  SELECTED_REGION,
  TabEnum,
} from '@wilson/shift-timeline/services';
import {
  SetCellViewUnassignedRegionHeightPx,
  SetSelectedUnassignedRegion,
  ShiftTimelineDataState,
  ShiftTimelineSettingsState,
  ShiftTimelineState,
  ShiftTimelineUnassignedRegionState,
  ShiftTimelineUnassignedRegionStateFacadeService,
  TimelineModalFilters,
  TimelineUpdateCellViewUnassignedShiftsRegionWithoutActivities,
  TimelineUpdateUnassignedRegionCollapsedViewShiftsCount,
  ToggleExpandUnassignedRegion,
  UnassignedRegionVisibleShiftsStateService,
} from '@wilson/shift-timeline/state';
import { addDays, isValid, subDays } from 'date-fns';
import { NzSegmentedModule } from 'ng-zorro-antd/segmented';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import {
  BehaviorSubject,
  Observable,
  Subscription,
  debounceTime,
  firstValueFrom,
  map,
  switchMap,
} from 'rxjs';
import { TimelineCommonOperationsService } from '../../../services/timeline-common-operations.service';
import { TimelineShiftDragAndDropDirectiveHelperService } from '../../../services/timeline-shift-drag-and-drop-directive-helper.service';
import { UnassignedRegionResizeService } from '../../../services/unassigned-region-resize.service';
import { UnassignShiftCommonOperationsService } from '../../../services/unassign-shift-common-operations.service';
import { GroupAssignmentHelperService } from '../../../components-v2/group-assignment-confirmation-modal/group-assignment-helper.service';
import { UnassignShiftDropHelperService } from '../../../components-v2/unassign-shift-drop-helper/unassign-shift-drop-helper.service';
import { TimelineShiftAssignService } from '../../../services/timeline-shift-assign.service';
import { AssignAllShiftsWithoutActivitiesButtonComponent } from '../assign-all-shifts-without-activities-button/assign-all-shifts-without-activities-button.component';
import { NewShiftToUnassignedRegionDropHelperService } from '../new-shift-to-unassigned-region-drop-helper/new-shift-to-unassigned-region-drop-helper.service';
import { UnassignedRegionSearchFilterComponent } from '../unassigned-region-search-filter/unassigned-region-search-filter.component';
import { UnassignedCellViewShiftsWithoutActivitiesDragHandlerService } from './unassigned-cell-view-shifts-without-activities-drag-handler.service';

@Component({
  selector: 'wilson-cell-view-timeline-unassigned-region',
  templateUrl: './cell-view-timeline-unassigned-region.component.html',
  styleUrls: ['./cell-view-timeline-unassigned-region.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FontAwesomeModule,
    NzSegmentedModule,
    FormsModule,
    NzTooltipModule,
    TranslateModule,
    LoadingSpinnerModule,
    UnassignedRegionCellViewShiftsDetailsRenderComponent,
    UnassignedRegionSegmentComponent,
    AssignAllShiftsWithoutActivitiesButtonComponent,
    UnassignedRegionSearchFilterComponent,
    FeatureFlagsModule,
    AuthorizationModule,
    UnassignedRegionToggleComponent,
  ],
  providers: [
    UnassignedCellViewShiftsWithoutActivitiesDragHandlerService,
    GroupAssignmentHelperService,
    UnassignShiftDropHelperService,
    TimelineShiftAssignService,
    NewShiftToUnassignedRegionDropHelperService,
    UnassignedRegionResizeService,
  ],
})
export class CellViewTimelineUnassignedRegionComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  private readonly store = inject(Store);
  protected readonly unassignedRegionResizeService = inject(
    UnassignedRegionResizeService,
  );
  private readonly route = inject(ActivatedRoute);
  private readonly timelineCommonOperationsService = inject(
    TimelineCommonOperationsService,
  );
  private readonly unassignedRegionVisibleShiftsStateService = inject(
    UnassignedRegionVisibleShiftsStateService,
  );
  private readonly checkShiftActivityPermissionsService = inject(
    CheckShiftActivityPermissionsService,
  );
  private readonly shiftTimelineUnassignedRegionStateFacadeService = inject(
    ShiftTimelineUnassignedRegionStateFacadeService,
  );
  protected readonly dragAndDropModeService = inject(DragAndDropModeService);
  private readonly createNewViaDragAndDropService = inject(
    CreateNewViaDragAndDropService,
  );
  protected readonly unassignShiftCommonOperationsService = inject(
    UnassignShiftCommonOperationsService,
  );
  private readonly unassignShiftDropHelperService = inject(
    UnassignShiftDropHelperService,
  );
  protected readonly timelineShiftDragAndDropDirectiveHelperService = inject(
    TimelineShiftDragAndDropDirectiveHelperService,
  );
  protected readonly unassignedCellViewShiftsWithoutActivitiesDragHandlerService =
    inject(UnassignedCellViewShiftsWithoutActivitiesDragHandlerService);
  private readonly newShiftToUnassignedRegionDropHelperService = inject(
    NewShiftToUnassignedRegionDropHelperService,
  );

  @Input()
  set scrollLeftPx(value: number) {
    this.scrollLeftPxSubject.next(value);
  }

  @Output()
  scrolling = new EventEmitter<number>();

  readonly heightAppliedManually = output<void>();

  @ViewChild('rightContainerWrapper')
  rightContainerWrapper!: ElementRef;

  @ViewChild(UnassignedRegionCellViewShiftsDetailsRenderComponent)
  private readonly detailsRenderComponent!: UnassignedRegionCellViewShiftsDetailsRenderComponent;

  private scrollLeftPxSubject = new BehaviorSubject(0);
  private subscriptions = new Subscription();
  private tabSubscription!: Subscription;
  private scrollDebouncerSubject = new BehaviorSubject<Event | null>(null);

  public resourcesColumnWidthPx$ = this.store.select(
    ShiftTimelineSettingsState.resourcesColumnWidthPx,
  );

  public frameLength$ = this.store.select(ShiftTimelineState.frameLength);

  isLoadingUnassignedShifts$!: Observable<boolean>;
  hideLoaderWhenLoadingUnassignedShiftsOrActivities$!: Observable<boolean>;

  filters$!: Observable<TimelineModalFilters>;

  unassignedShiftsWithoutActivities$!: Observable<UnassignedShift[]>;

  tabOptions = TabEnum;
  selectedTab$!: Observable<TabEnum>;
  isLoadingUsers$!: Observable<boolean>;
  isRefreshingUnassignedShifts$!: Observable<boolean>;
  isRefreshingUnassignedActivities$!: Observable<boolean>;
  isUnassignedRegionExpanded$!: Observable<boolean>;
  FILTER_PIXEL_ADJUSTMENT = 6;
  areActivitiesVisible$!: Observable<boolean>;

  protected readonly isExpanding = signal(false);
  protected readonly isHovered = signal(false);
  protected pixelWidthPerDay$ = this.store.select(
    ShiftTimelineSettingsState.pixelWidthPerDay(),
  );

  protected numberOfShiftPerDayRecord$!: Observable<NumberOfShiftPerDayRecord>;
  protected filteredUnassignedShiftStacks$!: Observable<
    UnassignedShiftStackByDay[]
  >;
  protected shiftIdsToHighlight$!: Observable<string[]>;
  protected RoleAction = RoleAction;
  protected RolePermissionSubject = RolePermissionSubject;
  protected can = inject(AbilityServiceSignal).can;
  protected visibleDaysRange$!: Observable<TimelineTimeframe>;
  protected timeframe$!: Observable<Interval>;
  protected unassignedRegionHeightPx$ = this.store.select(
    ShiftTimelineSettingsState.userCellViewUnassignedRegionHeightPx,
  );

  ngOnInit(): void {
    this.unassignedRegionResizeService.setHeightAction(
      SetCellViewUnassignedRegionHeightPx,
    );
    this.unassignedRegionResizeService.setToggleExpandedCallback((isExpanded) =>
      this.toggleRegion(isExpanded),
    );

    this.visibleDaysRange$ = this.store.select(
      ShiftTimelineDataState.visibleDateRange,
    );

    this.timeframe$ = this.store.select(ShiftTimelineDataState.timeframe);

    this.selectedTab$ = this.store.select(
      ShiftTimelineUnassignedRegionState.selectedRegion,
    );

    this.isLoadingUnassignedShifts$ = this.store.select(
      ShiftTimelineUnassignedRegionState.isLoadingUnassignedShifts,
    );

    this.hideLoaderWhenLoadingUnassignedShiftsOrActivities$ = this.store.select(
      ShiftTimelineUnassignedRegionState.hideLoaderWhenLoadingUnassignedShiftsOrActivities,
    );

    this.isRefreshingUnassignedShifts$ = this.store.select(
      ShiftTimelineUnassignedRegionState.isRefreshingUnassignedShifts,
    );

    this.unassignedShiftsWithoutActivities$ =
      this.shiftTimelineUnassignedRegionStateFacadeService.unassignedShiftsWithoutActivitiesOverlapLevels$.pipe(
        map((stacks) => {
          return stacks.flatMap((stack) => Object.values(stack.shifts));
        }),
      );

    this.isLoadingUsers$ = this.store.select(
      ShiftTimelineDataState.isLoadingUsers,
    );

    this.tabSubscription = this.route.queryParamMap.subscribe(async (data) => {
      const selectedTab = data.get(SELECTED_REGION);
      const currentSelectedTabInState = await firstValueFrom(this.selectedTab$);
      if (selectedTab && currentSelectedTabInState !== Number(selectedTab)) {
        this.store.dispatch(
          new SetSelectedUnassignedRegion(Number(selectedTab)),
        );
      }
    });

    this.isUnassignedRegionExpanded$ = this.store.select(
      ShiftTimelineUnassignedRegionState.isUnassignedRegionExpanded,
    );

    this.subscriptions.add(
      this.isUnassignedRegionExpanded$
        .pipe(
          switchMap((isExpanded) => {
            return this.timelineCommonOperationsService
              .createUnassignedRegionDataObservable()
              .pipe(map((interval) => ({ interval, isExpanded })));
          }),
        )
        .subscribe(({ interval, isExpanded }) => {
          if (isValid(interval.start) && isValid(interval.end)) {
            const payload = {
              start: subDays(interval.start, 1).getTime(),
              end: addDays(interval.end, 1).getTime(),
            };

            if (isExpanded) {
              this.store.dispatch(
                new TimelineUpdateCellViewUnassignedShiftsRegionWithoutActivities(
                  payload,
                ),
              );
            } else {
              this.store.dispatch(
                new TimelineUpdateUnassignedRegionCollapsedViewShiftsCount(
                  payload,
                ),
              );
            }
          }
        }),
    );

    this.subscriptions.add(
      this.scrollDebouncerSubject.pipe(debounceTime(10)).subscribe((event) => {
        // Debounce to prevent reflow on every single scroll event
        if (event) {
          this.scrolling.emit((event.target as HTMLElement).scrollLeft);
        }
      }),
    );

    this.numberOfShiftPerDayRecord$ = this.store.select(
      ShiftTimelineUnassignedRegionState.numberOfShiftPerDayRecords,
    );
    this.filteredUnassignedShiftStacks$ =
      this.shiftTimelineUnassignedRegionStateFacadeService.filteredUnassignedShiftsStacksWithoutActivities$;
    this.shiftIdsToHighlight$ = this.store.select(
      ShiftTimelineDataState.shiftIdsToHighlight,
    );
  }

  updateScrollingPosition(event: Event): void {
    // We ignore non-user input scrolling when expanding unassigned region
    if (!this.isExpanding()) {
      this.scrollDebouncerSubject.next(event);
    }
  }

  dragover(event: DragEvent): void {
    event.preventDefault();
    if (!this.isHovered()) this.isHovered.set(true);
  }

  async drop(event: DragEvent): Promise<void> {
    event.preventDefault();
    // Must run before any `await` in this handler — event.currentTarget is null afterward,
    // and dragend (fired on the drag source right after drop) resets dragStartOffsetX to null.
    const rawOffsetX = getRawOffsetXFromEvent(event);
    const dragStartOffsetX =
      this.createNewViaDragAndDropService.getDragStartOffsetX();

    const draggingItemId = await firstValueFrom(
      this.dragAndDropModeService.draggingItemId$,
    );
    if (draggingItemId === CreateNewViaDragAndDropService.MOCK_SHIFT_ID) {
      await this.newShiftToUnassignedRegionDropHelperService.createShiftFromCellViewDrop(
        rawOffsetX,
        dragStartOffsetX,
      );
      this.isHovered.set(false);
      return;
    }

    const draggingShift = await firstValueFrom(
      this.dragAndDropModeService.draggingShiftV2$,
    );
    if (draggingShift) {
      const droppedShift = draggingShift;
      const isComingFromUnassignedShiftZone = draggingShift.userId
        ? false
        : true;

      if (!isComingFromUnassignedShiftZone) {
        await this.unassignShiftDropHelperService.handleCellViewShiftDrop(
          droppedShift,
          () => {
            firstValueFrom(this.isUnassignedRegionExpanded$).then(
              (isExpanded) => {
                if (!isExpanded) {
                  this.timelineCommonOperationsService.refreshUnassignedRegion();
                }
              },
            );
          },
        );
        this.isHovered.set(false);
        return;
      }
    }
    this.isHovered.set(false);
    this.timelineShiftDragAndDropDirectiveHelperService.handleCellViewDragEnd();
  }

  // Driven by the resize handle, which lives in the parent gantt chart so that it
  // doesn't overlap (and break dragleave detection of) this region's drop zone.
  onResizeStart(): void {
    this.unassignedRegionResizeService.onResizeStart();
  }

  onResize(deltaY: number): void {
    this.unassignedRegionResizeService.onResize(deltaY);
  }

  onResizeEnd(deltaY: number): void {
    this.unassignedRegionResizeService.onResizeEnd(deltaY);
  }

  toggleRegion(isOpened: boolean): void {
    this.store.dispatch(new ToggleExpandUnassignedRegion(isOpened));
    this.isExpanding.set(true);
    this.timelineCommonOperationsService.refreshUnassignedRegion();

    setTimeout(() => {
      this.isExpanding.set(false);
    }, 100);
  }

  protected manualToggleRegion(isOpened: boolean): void {
    this.toggleRegion(isOpened);
    const heightPx = isOpened
      ? (this.store.selectSnapshot(
          ShiftTimelineSettingsState.userCellViewUnassignedRegionHeightPx,
        ) ?? this.defaultCellViewExpandedHeightPx())
      : MIN_UNASSIGNED_REGION_HEIGHT_PX;
    this.unassignedRegionResizeService.applyHeightPx(heightPx);
    this.heightAppliedManually.emit();
  }

  private defaultCellViewExpandedHeightPx(): number {
    const windowHeight = this.store.selectSnapshot(
      ShiftTimelineSettingsState.windowHeight,
    );
    return (windowHeight * CELL_VIEW_EXPANDED_HEIGHT_VH) / 100;
  }

  ngAfterViewInit(): void {
    this.unassignedRegionResizeService.setExtraSizedElements([
      {
        element:
          this.detailsRenderComponent.rightHeaderElementRef.nativeElement,
        property: 'height',
      },
    ]);
    const isExpanded = this.store.selectSnapshot(
      ShiftTimelineUnassignedRegionState.isUnassignedRegionExpanded,
    );
    this.unassignedRegionResizeService.applyHeightPx(
      isExpanded
        ? (this.store.selectSnapshot(
            ShiftTimelineSettingsState.userCellViewUnassignedRegionHeightPx,
          ) ?? this.defaultCellViewExpandedHeightPx())
        : MIN_UNASSIGNED_REGION_HEIGHT_PX,
    );

    this.subscriptions.add(
      this.scrollLeftPxSubject.subscribe((scrollLeft: number) => {
        this.rightContainerWrapper.nativeElement.scrollLeft = scrollLeft;
      }),
    );
  }

  handleVisibleShiftIds(
    visibleShiftIds: {
      shiftId: string;
      isVisible: boolean;
    }[],
  ): void {
    this.unassignedRegionVisibleShiftsStateService.updateVisibility(
      visibleShiftIds,
    );
  }

  ngOnDestroy(): void {
    this.subscriptions?.unsubscribe();
    this.tabSubscription.unsubscribe();
  }

  dragleave(event: DragEvent): void {
    event.preventDefault();
    const isCursorOutsideDropZone =
      this.unassignShiftCommonOperationsService.isCursorOutsideDropZone(event);
    if (isCursorOutsideDropZone) {
      this.isHovered.set(false);
    }
  }
}
